import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

interface StatusDotProps {
  /** Visual tone of the dot */
  tone: "active" | "muted";
}

/**
 * Small circular status indicator. Active is tinted emerald to match the
 * status badge; muted is a low-contrast neutral.
 * @param props - StatusDotProps
 * @returns Circular indicator element
 */
export function StatusDot({ tone }: StatusDotProps) {
  return (
    <span
      aria-hidden
      className={cn(
        "inline-block h-1.5 w-1.5 rounded-full",
        tone === "active" ? "bg-emerald-500" : "bg-muted-foreground/40",
      )}
    />
  );
}

interface StatusBadgeProps {
  /** Whether the sync is currently enabled */
  enabled: boolean;
}

/**
 * Header status badge for a sync, tinted to match the rest of the settings
 * surfaces in the app.
 * @param props - StatusBadgeProps
 * @returns Badge element
 */
export function StatusBadge({ enabled }: StatusBadgeProps) {
  return (
    <Badge
      variant="secondary"
      className={cn(
        "gap-1.5 transition-colors duration-200",
        enabled
          ? "border-emerald-500/20 bg-emerald-500/10 text-emerald-600"
          : "border-border bg-transparent text-muted-foreground",
      )}
    >
      <StatusDot tone={enabled ? "active" : "muted"} />
      {enabled ? "Enabled" : "Disabled"}
    </Badge>
  );
}
